import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Building2, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OnboardingFields, onboardingMissing } from "@/components/OnboardingForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicOnboarding() {
  const { token } = useParams();
  const [info, setInfo] = useState(undefined); // undefined=loading, null=invalid
  const [intake, setIntake] = useState({});
  const [directors, setDirectors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/onboarding/${token}`).then((r) => {
      setInfo(r.data);
      const o = r.data.owner || {};
      const seeded = { ...(r.data.intake || {}) };
      if (!seeded.owner_name && o.name) seeded.owner_name = o.name;
      if (!seeded.owner_email && o.email) seeded.owner_email = o.email;
      if (!seeded.owner_mobile && o.phone) seeded.owner_mobile = o.phone;
      if (!seeded.owner_home_address && o.address) seeded.owner_home_address = o.address;
      setIntake(seeded);
      setDirectors(r.data.directors || []);
    }).catch(() => setInfo(null));
  }, [token]);

  const submit = async () => {
    const missing = onboardingMissing(info.form, intake, "landlord");
    if (missing.length) { setErr(`Please complete: ${missing.join(", ")}.`); return; }
    setSaving(true); setErr("");
    try { await axios.put(`${API}/public/onboarding/${token}`, { intake, directors }); setDone(true); }
    catch (e) { setSaving(false); setErr(e?.response?.data?.detail || "Sorry, we couldn't save your answers just now. Please try again."); }
  };

  if (info === undefined) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (info === null) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] p-6"><p className="text-center text-muted-foreground">This onboarding link is invalid or has expired.</p></div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-md bg-[#18181A] flex items-center justify-center"><Building2 className="h-5 w-5 text-white" /></div>
          <span className="font-head text-xl font-extrabold tracking-tight">{info.brand_name}</span>
        </div>
        {done ? (
          <div className="bg-white border border-border rounded-md p-8 text-center" data-testid="onboarding-thanks">
            <CheckCircle2 className="h-10 w-10 text-[#2B4C3F] mx-auto mb-3" />
            <h1 className="font-head text-2xl font-extrabold">Thank you</h1>
            <p className="text-sm text-muted-foreground mt-2">Your onboarding details for <strong>{info.property_title}</strong> have been sent to our team. We'll be in touch with the next steps.</p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-md p-6">
            <p className="overline text-accent">New client onboarding</p>
            <h1 className="font-head text-2xl font-extrabold tracking-tight mt-1">{info.property_title}</h1>
            <p className="text-sm text-muted-foreground mt-1 mb-6">{info.address} · Please complete the details below so we can get your property ready to go live.</p>
            <OnboardingFields schema={info.form} intake={intake} setIntake={setIntake} directors={directors} setDirectors={setDirectors} mode="landlord" />
            <Button data-testid="public-onboarding-submit" className="w-full mt-6" disabled={saving} onClick={submit}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit my details"}
            </Button>
            {err && <p data-testid="onboarding-error" className="text-xs text-[#9E382A] text-center mt-2">{err}</p>}
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground mt-4">Powered by {info.brand_name} · This is a private link just for you.</p>
      </div>
    </div>
  );
}
